/* eslint-disable @typescript-eslint/no-unused-vars */
import type { SyntheticEvent } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Link as MuiLink } from '@material-ui/core';
import type {
    CommonProps,
    OverridableTypeMap,
} from '@material-ui/core/OverridableComponent';

import { useKlpStyles, removeKlpStyles } from '../Theme';
import type { IKlpStyles } from '../Theme';

interface IOwnProps {
    /**
     * Element placed before the children.
     */
    startIcon?: React.ReactNode;
    /**
     * Element placed after the children.
     */
    endIcon?: React.ReactNode;
    /**
     * The URL to link to when the button is clicked.
     * If defined, an `a` element will be used as the root node.
     */
    href?: string;
    target?: string;
    translation?: string;
    translationOptions?: Record<string, any>;
    onClick?: (event: SyntheticEvent) => void;
    disabled?: boolean;
}

const styles = () => ({
    link: {
        cursor: 'pointer',
    },
    linkDisabled: {
        cursor: 'default',
        opacity: 0.5,
    },
});

export type ILinkProps = IOwnProps &
    IKlpStyles &
    CommonProps<OverridableTypeMap>;

export const Link: React.FC<ILinkProps> = (props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const classes = useKlpStyles(props, styles);

    const {
        children,
        href,
        target,
        startIcon,
        endIcon,
        translation,
        translationOptions,
        onClick,
        className,
        disabled,
        ...restPropsRaw
    } = props;

    const restProps = { ...removeKlpStyles(restPropsRaw) };

    const handleClick = (event: SyntheticEvent) => {
        event.preventDefault();

        if (disabled) {
            return;
        }

        if (onClick) {
            onClick(event);
        }
        if (!href) {
            return;
        }

        if (href.match(/mailto/g) || href.match(/tel/g)) {
            window.open(href, target);
        } else if (!href.match(/http/g)) {
            history.push(href);
        } else if (target) {
            window.open(href, target);
        } else {
            window.location.href = href;
        }
    };

    return startIcon || endIcon ? (
        <div className={classes.link} onClick={handleClick}>
            {startIcon}
            <MuiLink
                href={href}
                target={target}
                onClick={handleClick}
                className={`${className} ${classes.link} ${disabled ? classes.linkDisabled : ''}`}
                aria-disabled={disabled || undefined}
                {...restProps}
            >
                {translation
                    ? t(translation, translationOptions)
                    : children
                      ? children
                      : ''}
            </MuiLink>
            {endIcon}
        </div>
    ) : (
        <MuiLink
            href={href}
            target={target}
            onClick={handleClick}
            className={`${className} ${classes.link} ${disabled ? classes.linkDisabled : ''}`}
            aria-disabled={disabled || undefined}
            {...restProps}
        >
            {translation
                ? t(translation, translationOptions)
                : children
                  ? children
                  : ''}
        </MuiLink>
    );
};
